import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  cpGetDomainStatus,
  cpSetCustomDomain,
  cpVerifyDomain,
  cpRemoveDomain,
  type SetDomainResponse,
} from '@/lib/api/controlPlaneClient'

const DOMAIN_QUERY_KEY = ['custom-domain-status'] as const

const POLLING_STATES = ['provisioning_ssl']

export function useCustomDomain() {
  const queryClient = useQueryClient()
  const [verifyResultError, setVerifyResultError] = useState<string | null>(null)

  const statusQuery = useQuery({
    queryKey: DOMAIN_QUERY_KEY,
    queryFn: cpGetDomainStatus,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status && POLLING_STATES.includes(status) ? 5000 : false
    },
  })

  const setDomainMutation = useMutation({
    mutationFn: (domain: string) => cpSetCustomDomain(domain),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOMAIN_QUERY_KEY })
    },
  })

  const verifyMutation = useMutation({
    mutationFn: () => cpVerifyDomain(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: DOMAIN_QUERY_KEY })
      setVerifyResultError(data.error ?? null)
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => cpRemoveDomain(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOMAIN_QUERY_KEY })
    },
  })

  return {
    domain: statusQuery.data?.domain ?? null,
    status: statusQuery.data?.status ?? 'none',
    storefrontUrl: statusQuery.data?.storefront_url ?? null,
    isLoading: statusQuery.isLoading,

    setDomain: setDomainMutation.mutate,
    setDomainAsync: setDomainMutation.mutateAsync,
    verify: verifyMutation.mutate,
    remove: removeMutation.mutate,

    isSettingDomain: setDomainMutation.isPending,
    isVerifying: verifyMutation.isPending,
    isRemoving: removeMutation.isPending,

    setDomainError: setDomainMutation.error?.message ?? null,
    verifyError: verifyMutation.error?.message ?? verifyResultError,

    setDomainData: setDomainMutation.data as SetDomainResponse | undefined,
  }
}
